/**
 * Kiosk Settings Types & Enums
 * Comprehensive type definitions for EV operator kiosk configuration
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Status of a charging connector that should be interpreted as "Connected"
 */
export enum ConnectedStatusEnum {
  Available = 'Available',
  Occupied = 'Occupied',
  Reserved = 'Reserved',
  Unavailable = 'Unavailable',
  Faulted = 'Faulted',
  Charging = 'Charging',
}

/**
 * Allowed character types for license plate validation
 */
export enum AllowedCharactersEnum {
  Alphanumeric = 0,
  Numeric = 1,
  Alphabetic = 2,
  Unknown = 3 // Excluded from UI
}

/**
 * RFID card authorisation flow support
 * Java enum: _UNKNOWN(null), REMOTE_START("REMOTE_START")
 */
export enum SupportRfidFlowEnum {
  REMOTE_START = 'REMOTE_START',
}

/**
 * Type of action to display after plug-in
 */
export enum ConnectedActionEnum {
  Disabled = 'Disabled',
  Payment = 'Payment',
  Pricing = 'Pricing',
  Unknown = 'Unknown'
}

// ============================================================================
// NESTED INTERFACES
// ============================================================================

/**
 * Idle screen settings - displayed when kiosk is not in use
 */
export interface IdleScreenSettings {
  enabled: boolean;
  imageUrl?: string;
  /**
   * Timeout in milliseconds (API format)
   * UI: converted to seconds
   * API: stored as milliseconds
   */
  timeout: number;
}

/**
 * Action to display after connector is plugged in
 */
export interface ConnectedActionSettings {
  type: ConnectedActionEnum;
  /**
   * Time in milliseconds (API format)
   * UI: converted to seconds
   * API: stored as milliseconds
   * Required when type === ConnectedActionEnum.Pricing
   */
  time?: number;
}

/**
 * License plate capture and validation settings
 */
export interface LicensePlateSettings {
  showLicensePlate: boolean;
  isMandatory: boolean;
  allowedCharacters?: AllowedCharactersEnum;
}

// ============================================================================
// MAIN DTO
// ============================================================================

/**
 * Complete kiosk settings data transfer object
 * All fields organized by configuration category
 */
export interface KioskSettingsDto {
  // ──────────────────────────────────────────────────────────────────────
  // METADATA (From DbActor model)
  // ──────────────────────────────────────────────────────────────────────

  /** Operator ID - Display only, cannot be edited */
  operatorId: number;

  /** Country code from DbActor model (e.g., "IL", "US") - Required for updates */
  countryCode?: string;

  /** Party ID from DbActor model (e.g., "SNL", "NayaxVend") - Required for updates */
  partyId?: string;

  // ──────────────────────────────────────────────────────────────────────
  // GENERAL SETTINGS
  // ──────────────────────────────────────────────────────────────────────

  /** CPO (Charge Point Operator) password - 6-10 digits numeric */
  cpoPassword?: string;

  /**
   * Whether connection-status-based features are supported.
   * Defaults to true when absent. When explicitly false, the following fields
   * are disabled: connectionRequired, isConnectedSorting, connectedAction.
   */
  connectedStatusSupport?: boolean;

  /** Status interpreted as "Connected" in the UI */
  connectedStatus?: ConnectedStatusEnum | null;

  /** Logo URL to display in kiosk interface */
  logoUrl?: string;

  // ──────────────────────────────────────────────────────────────────────
  // LANGUAGE SETTINGS
  // ──────────────────────────────────────────────────────────────────────

  /** Default language (language code: en, he, es, etc) */
  language: string;

  /** Array of supported languages for kiosk display */
  supportedLanguages: string[];

  // ──────────────────────────────────────────────────────────────────────
  // SCREEN BEHAVIOR SETTINGS
  // ──────────────────────────────────────────────────────────────────────

  /** Dynamic connectors - Sort connectors by connection status */
  isConnectedSorting: boolean;

  /** Tariff Time Display Mode — false = Relative, true = Exact */
  tariff24HoursDisplayNonRelative: boolean;

  /** Present Start Button on Connection Only */
  connectionRequired: boolean;

  /** Hide Stop Button from display */
  hideStopButton: boolean;

  /** Idle screen configuration */
  idleScreen: IdleScreenSettings;

  /**
   * Session timeout in milliseconds (API format)
   * UI: converted to seconds (30-300 range)
   * Range: 30,000 - 300,000 milliseconds
   */
  sessionTimeout: number;

  /** Action to display after plug-in */
  connectedAction: ConnectedActionSettings;

  // ──────────────────────────────────────────────────────────────────────
  // PAYMENT SETTINGS
  // ──────────────────────────────────────────────────────────────────────

  /** Minimum cost per minute of charging */
  minimumCostPerMinute?: number;

  /** Enable incremental authorization for payment */
  isIncrementalAuth: boolean;

  /**
   * AC Credit Buffer - Percentage of authorized amount
   * Range: 0 - 99.9 (with 1 decimal place)
   * Default: 1
   * Determines when to stop charging to prevent exceeding authorized amount
   */
  acCreditBuffer: number;

  /**
   * DC Credit Buffer - Percentage of authorized amount
   * Range: 0 - 99.9 (with 1 decimal place)
   * Default: 2
   * Determines when to stop charging to prevent exceeding authorized amount
   */
  dcCreditBuffer: number;

  /** Maximum transaction fee allowed */
  maxTransactionFee?: number;

  /**
   * RFID card authorisation flow support
   * null = _UNKNOWN (not configured), 'REMOTE_START' = remote start flow
   */
  supportRfidFlow?: SupportRfidFlowEnum | null;

  /**
   * Minimum auth approval ratio (0–1).
   * Blocks session start if approved_amount < requested_amount * ratio.
   * 0 = feature disabled. Default: 0.10
   */
  authApprovalMinRatio?: number;

  /**
   * Average cost per kWh
   * Range: 0.1 - 99999 (with 2 decimal places)
   * Used for cost estimation
   */
  averageKwhCost?: number;

  // ──────────────────────────────────────────────────────────────────────
  // LICENSE PLATE SETTINGS
  // ──────────────────────────────────────────────────────────────────────

  /** License plate capture and validation settings */
  licensePlate: LicensePlateSettings;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Request payload for updating kiosk settings
 * Only includes changed fields (delta object)
 */
export interface UpdateKioskSettingsRequest {
  [key: string]: any;
}

/**
 * API Response wrapper (if needed for error handling)
 */
export interface KioskSettingsApiResponse {
  success: boolean;
  data?: KioskSettingsDto;
  error?: string;
}

// ============================================================================
// UI STATE TYPES
// ============================================================================

/**
 * Store state for kiosk settings management
 */
export interface KioskSettingsState {
  settings: KioskSettingsDto | null;
  originalSettings: KioskSettingsDto | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  isDialogOpen: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Supported language options — matches Flutter's appsettings.json kioskSupportedLangs
 * ["he","en","de","ru","pt","fr","hu","es","ca","en-gb","pl"], sorted A-Z by name
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'ca',    name: 'Catalan' },
  { code: 'en',    name: 'English' },
  { code: 'en-gb', name: 'English (UK)' },
  { code: 'fr',    name: 'French' },
  { code: 'de',    name: 'German' },
  { code: 'he',    name: 'Hebrew' },
  { code: 'hu',    name: 'Hungarian' },
  { code: 'pl',    name: 'Polish' },
  { code: 'pt',    name: 'Portuguese' },
  { code: 'ru',    name: 'Russian' },
  { code: 'es',    name: 'Spanish' },
];

/** Session timeout constraints */
export const SESSION_TIMEOUT_CONSTRAINTS = {
  MIN_SECONDS: 30,
  MAX_SECONDS: 300,
  DEFAULT_SECONDS: 60
};

/** Credit buffer constraints */
export const CREDIT_BUFFER_CONSTRAINTS = {
  MIN: 0,
  MAX: 99.9,
  AC_DEFAULT: 1,
  DC_DEFAULT: 2
};

 /** Auth approval minimum ratio constraints */
export const AUTH_APPROVAL_MIN_RATIO_CONSTRAINTS = {
  MIN: 0,
  MAX: 100,
  DEFAULT: 10,
  STEP: 1,
};

/** Average kWh cost constraints */
export const AVERAGE_KWH_COST_CONSTRAINTS = {
  MIN: 0.1,
  MAX: 99999,
  DECIMAL_PLACES: 2
};

/** CPO Password constraints */
export const CPO_PASSWORD_CONSTRAINTS = {
  MIN_LENGTH: 6,
  MAX_LENGTH: 10,
  REGEX: /^\d{6,10}$/,
  ERROR_MESSAGE: 'Must be 6-10 digits'
};

/** Time conversion constants */
export const TIME_CONVERSION = {
  MS_PER_SECOND: 1000,
  SECONDS_TO_MS: (seconds: number) => seconds * 1000,
  MS_TO_SECONDS: (ms: number) => ms / 1000
};
